"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { useRefresh } from "react-admin";
import { toast } from "sonner";
import TourForm from "./TourForm";
import axiosInstance from "@/utils/axiosInstance";
import { Tour } from "@/interface/entityInterface";
import { useUserContext } from "@/context/UserContext";

interface Props {
  details: Tour;
  photos: any[];
  setPhotos: (photos: any[]) => void;
}

const Overview: React.FC<Props> = ({ details, photos, setPhotos }) => {
  const refresh = useRefresh();
  const { decodedToken } = useUserContext();

  const [loading, setLoading] = useState(false);
  const [formProp, setFormProp] = useState({
    name: details?.name,
    duration: details?.duration,
    tour_type: details?.tour_type,
    max_group_size: details?.max_group_size,
    pickup_latitude: details?.pickup_latitude,
    pickup_longitude: details?.pickup_longitude,
    dropoff_latitude: details?.dropoff_latitude,
    dropoff_longitude: details?.dropoff_longitude,
    dropoff: details?.dropoff,
    includes: details?.includes,
    currency: details?.currency,
    images: [],
  });

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.put(`/tours/${details.id}`, formProp);
      toast("Tour updated successfully");
      refresh();
    } catch {
      toast("An error occured. Please try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFormProp({
      ...formProp,
      images: photos,
    });
  }, [photos]);

  const handleChange = (index: number, value: string) => {
    const newPaths = [...photos];
    newPaths[index] = {
      ...newPaths[index],
      imageUrl: value,
      key: `photo_${index + 1}`,
    };

    setPhotos(newPaths);
  };

  useEffect(() => {
    getLocation({
      text: details.name,
      lat: details.dropoff_latitude,
      lng: details.dropoff_longitude,
    });
  }, [details.id]);

  const getLocation = (location: any) => {
    setFormProp((prev: any) => {
      return {
        ...prev,
        dropoff: location.text,
        dropoff_latitude: location.lat,
        dropoff_longitude: location.lng,
      };
    });
  };

  return (
    <div className="w-full">
      <form onSubmit={submitForm} className="pr-4 pt-5">
        <TourForm
          getLocation={getLocation}
          formProp={formProp}
          setFormProp={setFormProp}
        />

        <div className="mt-12 grid grid-cols-2 place-items-center gap-x-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="w-full mt-1 flex flex-col items-start">
              <Label
                htmlFor={`photo_${i}`}
                className="mb-2 text-left w-full sm:w-auto"
              >
                Photo {i + 1} *
              </Label>
              <Input
                id={`photo_${i}`}
                type="text"
                placeholder={`Photo ${i + 1}`}
                className="mb-5"
                value={photos[i]?.imageUrl || ""}
                onChange={(e) => handleChange(i, e.target.value)}
                disabled={decodedToken?.permissions.user_role !== "owner"}
              />
            </div>
          ))}
        </div>

        {decodedToken?.permissions.user_role === "owner" && (
          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default Overview;
